// `chrome-extension.jsp:359-385` + `services/mouseflow-service/mouseflow.service.ng1.ts`.
//
// Session recording, which upstream splits across two places that are easy to mistake for one:
// the JSP LOADS the recorder and identifies the user, and a service STARTS and STOPS it. Both
// halves are needed — either alone records nothing.
//
// ⚠️ INSTALLED OUTSIDE REACT, like `installAuthStateListener` and for the same reason: upstream
// put this in the page shell, so it covers every chrome-extension screen. There are five
// top-level route branches and no component common to all of them, so a hook or a layout would
// cover some screens and not others.
//
// ⚠️ AND WHAT IT RECORDS IS CANDIDATE DATA. The panel shows names, emails, phone numbers and CV
// text, so these sessions carry third-party personal data. That is a product decision (kept,
// deliberately, 15 Oct) rather than a technical one, and it is the reason this file says so.
import { IFrameEventType, iframeClient, isInIFrame } from '@woo-ext/protocol'
import { fetchViewModel } from '@woo-ext/api'

/**
 * The two project ids, chosen SERVER-SIDE upstream by `Constants.IS_DEV || Constants.IS_TEST`
 * (the JSP interpolates one or the other into the script URL). They are not secrets — any
 * browser loading the tenant can read both out of the page.
 */
export const MOUSEFLOW_PROJECT = {
  nonProd: '021fe039-9637-4dd5-8c2b-4ef0467f9c77',
  prod: 'f7d00d85-92d0-42af-9730-908ec3fc05d8',
} as const

/** `IS_DEV || IS_TEST` picks the non-prod project; everything else is prod. */
export function mouseflowProjectId(env: { IS_DEV?: boolean; IS_TEST?: boolean } | undefined): string {
  return env?.IS_DEV === true || env?.IS_TEST === true ? MOUSEFLOW_PROJECT.nonProd : MOUSEFLOW_PROJECT.prod
}

/**
 * `getCookie` from the JSP, ported including its quirk.
 *
 * ⚠️ IT RETURNS `undefined` UNLESS THE SPLIT YIELDS EXACTLY TWO PARTS. A cookie whose name
 * appears twice — or a value containing the `; name=` sequence — yields three and upstream
 * answers nothing rather than guessing. Copied rather than "fixed": identifying a session under
 * the wrong uid is worse than not identifying it.
 */
export function readCookie(name: string, cookie: string): string | undefined {
  const parts = `; ${cookie}`.split(`; ${name}=`)
  if (parts.length !== 2) return undefined
  return parts.pop()?.split(';').shift()
}

interface MouseflowWindow {
  _mfq?: unknown[]
  mouseflowAutoStart?: boolean
  mouseflow?: { start?: () => void; stop?: () => void }
}

/**
 * The JSP's inline block: the global queue, `mouseflowAutoStart = false`, and a deferred script
 * appended to `<head>`.
 *
 * ⚠️ `mouseflowAutoStart = false` IS NOT "DISABLED", which is the obvious misreading and the one
 * that would make someone delete this. It stops the recorder starting on page load; the service
 * below starts it when the shortlist panel opens, so recording spans the panel's lifetime
 * rather than the page's.
 */
function loadRecorder(projectId: string, doc: Document): void {
  const win = doc.defaultView as unknown as MouseflowWindow | null
  if (win === null) return
  win.mouseflowAutoStart = false
  win._mfq = win._mfq ?? []

  const script = doc.createElement('script')
  script.type = 'text/javascript'
  script.defer = true
  // Protocol-relative upstream (`//cdn.mouseflow.com/...`). Written https here because this
  // app is only ever served over https and a protocol-relative URL is a downgrade waiting to
  // happen on a http origin.
  script.src = `https://cdn.mouseflow.com/projects/${projectId}.js`
  doc.head.append(script)
}

/** `_mfq.push(["identify", uid])` + `setVariable uuid` — both, as upstream sends both. */
function identify(uid: string, win: MouseflowWindow): void {
  win._mfq = win._mfq ?? []
  win._mfq.push(['identify', uid])
  win._mfq.push(['setVariable', 'uuid', uid])
}

export interface InstallMouseflowOptions {
  /** Injected for the test; defaults to the real bootstrap. */
  loadEnv?: () => Promise<{ IS_DEV?: boolean; IS_TEST?: boolean } | undefined>
  doc?: Document
}

/**
 * Install both halves. Returns a dispose for the subscription.
 *
 * ⚠️ THE NOTIFICATIONS FRAME IS SKIPPED, as upstream skips it
 * (`mouseflow.service.ng1.ts:22`). It is a hidden second iframe; recording it would produce
 * sessions of a blank document that nobody can interpret.
 *
 * ⚠️ AND THE PROJECT ID COSTS ONE REQUEST HERE THAT UPSTREAM GOT FREE. The JSP knew the server's
 * environment at render time; this app has to ask `/extension/view-model` for `ENV`. The script
 * is deferred anyway, so the cost is a request rather than a delay — but it is a real difference
 * and it is why this is not simply a copied `<script>` tag in `index.html`.
 */
export function installMouseflow(options: InstallMouseflowOptions = {}): () => void {
  const doc = options.doc ?? document
  const win = doc.defaultView as unknown as MouseflowWindow | null
  if (win === null) return () => undefined

  const frameRole = new URLSearchParams(doc.defaultView?.location.search ?? '').get('frameRole')
  if (frameRole === 'notifications') return () => undefined

  const loadEnv = options.loadEnv ?? (async () => (await fetchViewModel()).ENV)
  void loadEnv().then(
    (env) => {
      loadRecorder(mouseflowProjectId(env), doc)
      const uid = readCookie('uid', doc.cookie)
      if (uid !== undefined && uid.length > 0) identify(uid, win)
    },
    () => {
      // A bootstrap that will not answer is already fatal for the screens; recording is not
      // the thing to escalate it, and upstream's own start/stop is wrapped in try/catch.
    },
  )

  // `initParentMessageListeners` — the panel's open/closed state arrives from the content
  // script, so outside a frame there is nothing to listen to and nothing ever starts.
  if (!isInIFrame()) return () => undefined

  return iframeClient().onMessage((message) => {
    if (message.type !== IFrameEventType.ShortlistOpenedOrClosed) return
    const isOpened = (message.data as { isOpened?: boolean } | undefined)?.isOpened
    // ⚠️ BOTH CALLS ARE GUARDED AND SWALLOWED, as upstream's are. The recorder may not have
    // loaded — a blocked CDN, an ad-blocker, a slow network — and a recruiter's screen must not
    // break because session recording did not arrive.
    try {
      const recorder = win.mouseflow
      if (isOpened === true) recorder?.start?.()
      else recorder?.stop?.()
    } catch {
      // Upstream logs; there is no logger on this path here and a console error on every
      // panel toggle would be worse than silence.
    }
  })
}
